import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

function DeveloperDocs() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": "SharePanda API Documentation - File Upload Guide",
    "description": "Complete guide to uploading and managing files on SharePanda using our REST API",
    "author": {
      "@type": "Organization",
      "name": "SharePanda"
    },
    "datePublished": "2025-10-22",
    "url": "https://sharepanda.org/docs"
  };

  return (
    <>
      <SEO 
        title="Developer API Documentation"
        description="Learn how to upload and manage files on SharePanda using our REST API. Complete guide with code examples in multiple languages."
        keywords="API documentation, file upload API, REST API, developer guide, SharePanda API, file distribution API"
        url="https://sharepanda.org/docs"
        jsonLd={jsonLd}
      />
      <div className="docs-page">
        <div className="container">
          <div className="docs-header">
            <h1>Developer API Documentation</h1>
            <p className="docs-subtitle">
              Learn how to integrate SharePanda file distribution into your applications
            </p>
          </div>

          {/* Static Sites */}
          <section className="docs-section">
            <h2>🆕 Static Site Hosting</h2>
            <p>
              Deploy static websites from your terminal with the SharePanda CLI. Get a free
              <code>https://&lt;project&gt;.sharepanda.org</code> domain, add a custom domain, and
              rollback releases instantly.
            </p>
            <Link to="/static-sites" className="btn btn-primary btn-sm">
              View Static Site Docs
            </Link>
          </section>

          {/* Quick Start */}
          <section className="docs-section">
            <h2>Quick Start</h2>
            <p>
              SharePanda provides a simple REST API for uploading and managing files. 
              Get started in minutes with our straightforward authentication and file upload endpoints.
            </p>
            
            <div className="info-box">
              <strong>Base URL:</strong> <code>https://sharepanda.org/api</code>
            </div>
          </section>

          {/* Authentication */}
          <section className="docs-section">
            <h2>Authentication</h2>
            <p>SharePanda supports two authentication methods:</p>

            <h3>1. API Keys (Recommended for Scripts & CI/CD)</h3>
            <p>
              Generate an API key from your <Link to="/api-keys">API Keys page</Link> after signing up.
              Include it in the <code>X-API-Key</code> header.
            </p>

            <div className="code-block">
              <div className="code-header">
                <span>HTTP Header</span>
              </div>
              <pre><code>X-API-Key: apk_your_api_key_here</code></pre>
            </div>

            <h3>2. Session Cookies (For Web Applications)</h3>
            <p>
              Login via the web interface or API, and cookies will be automatically managed.
            </p>
          </section>

          {/* Getting Started */}
          <section className="docs-section">
            <h2>Step 1: Register & Get API Key</h2>
            
            <h3>Create an Account</h3>
            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -X POST https://sharepanda.org/api/auth/register \\
  -H "Content-Type: application/json" \\
  -d '{
    "username": "your_username",
    "password": "your_secure_password",
    "email": "you@example.com"
  }'`}</code></pre>
            </div>

            <h3>Generate API Key</h3>
            <ol>
              <li>Login to <a href="https://sharepanda.org/login" target="_blank" rel="noopener noreferrer">SharePanda</a></li>
              <li>Navigate to <Link to="/api-keys">API Keys</Link> page</li>
              <li>Click "Create New API Key"</li>
              <li>Give it a name (e.g., "CI/CD Pipeline")</li>
              <li>Copy and securely store your API key</li>
            </ol>

            <div className="warning-box">
              <strong>Important:</strong> Store your API key securely. It won't be shown again after creation.
              Never commit API keys to version control.
            </div>
          </section>

          {/* File Upload */}
          <section className="docs-section">
            <h2>Step 2: Upload Files</h2>
            
            <h3>Upload Endpoint</h3>
            <div className="endpoint-box">
              <span className="method post">POST</span>
              <code>/api/files/upload</code>
            </div>

            <h3>Request Parameters</h3>
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>file</code></td>
                  <td>File</td>
                  <td>Yes</td>
                  <td>The file(s) to upload (multipart/form-data). Can be sent multiple times for batch uploads.</td>
                </tr>
                <tr>
                  <td><code>is_public</code></td>
                  <td>Boolean</td>
                  <td>No</td>
                  <td>Make file(s) publicly accessible (default: false). Applies to all files in the request.</td>
                </tr>
              </tbody>
            </table>

            <div className="info-box">
              <strong>✨ New:</strong> Multiple file upload is now supported! Send multiple files in a single request for batch uploads.
            </div>

            <h3>Examples</h3>

            <h4>Single File Upload</h4>
            <div className="code-block">
              <div className="code-header">
                <span>Bash</span>
                <button className="copy-btn" onClick={() => navigator.clipboard.writeText(`curl -X POST https://sharepanda.org/api/files/upload \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -F "file=@/path/to/your/app.apk" \\
  -F "is_public=true"`)}>Copy</button>
              </div>
              <pre><code>{`curl -X POST https://sharepanda.org/api/files/upload \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -F "file=@/path/to/your/app.apk" \\
  -F "is_public=true"`}</code></pre>
            </div>

            <h4>Multiple File Upload (New!)</h4>
            <div className="code-block">
              <div className="code-header">
                <span>Bash</span>
                <button className="copy-btn" onClick={() => navigator.clipboard.writeText(`curl -X POST https://sharepanda.org/api/files/upload \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -F "file=@/path/to/app-v1.0.apk" \\
  -F "file=@/path/to/app-v1.0.ipa" \\
  -F "file=@/path/to/release-notes.pdf" \\
  -F "is_public=true"`)}>Copy</button>
              </div>
              <pre><code>{`curl -X POST https://sharepanda.org/api/files/upload \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -F "file=@/path/to/app-v1.0.apk" \\
  -F "file=@/path/to/app-v1.0.ipa" \\
  -F "file=@/path/to/release-notes.pdf" \\
  -F "is_public=true"`}</code></pre>
            </div>

            <h4>cURL</h4>
            <div className="code-block">
              <div className="code-header">
                <span>Bash</span>
                <button className="copy-btn" onClick={() => navigator.clipboard.writeText(`curl -X POST https://sharepanda.org/api/files/upload \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -F "file=@/path/to/your/app.apk" \\
  -F "is_public=true"`)}>Copy</button>
              </div>
              <pre><code>{`curl -X POST https://sharepanda.org/api/files/upload \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -F "file=@/path/to/your/app.apk" \\
  -F "is_public=true"`}</code></pre>
            </div>

            <h4>Python</h4>
            <div className="code-block">
              <div className="code-header">
                <span>Python - Single File</span>
                <button className="copy-btn" onClick={() => navigator.clipboard.writeText(`import requests

API_KEY = "apk_your_api_key_here"
FILE_PATH = "/path/to/your/app.apk"

headers = {
    "X-API-Key": API_KEY
}

with open(FILE_PATH, "rb") as file:
    files = {"file": file}
    data = {"is_public": "true"}
    
    response = requests.post(
        "https://sharepanda.org/api/files/upload",
        headers=headers,
        files=files,
        data=data
    )
    
    if response.status_code == 200:
        result = response.json()
        print(f"File uploaded! ID: {result['file_id']}")
        print(f"Message: {result['message']}")
    else:
        print(f"Error: {response.json()['error']}")`)}>Copy</button>
              </div>
              <pre><code>{`import requests

API_KEY = "apk_your_api_key_here"
FILE_PATH = "/path/to/your/app.apk"

headers = {
    "X-API-Key": API_KEY
}

with open(FILE_PATH, "rb") as file:
    files = {"file": file}
    data = {"is_public": "true"}
    
    response = requests.post(
        "https://sharepanda.org/api/files/upload",
        headers=headers,
        files=files,
        data=data
    )
    
    if response.status_code == 200:
        result = response.json()
        print(f"File uploaded! ID: {result['file_id']}")
        print(f"Message: {result['message']}")
    else:
        print(f"Error: {response.json()['error']}")`}</code></pre>
            </div>

            <div className="code-block">
              <div className="code-header">
                <span>Python - Multiple Files</span>
                <button className="copy-btn" onClick={() => navigator.clipboard.writeText(`import requests

API_KEY = "apk_your_api_key_here"
FILES_TO_UPLOAD = [
    "/path/to/app-v1.0.apk",
    "/path/to/app-v1.0.ipa",
    "/path/to/release-notes.pdf"
]

headers = {"X-API-Key": API_KEY}

# Open all files
file_handles = [('file', open(path, 'rb')) for path in FILES_TO_UPLOAD]

try:
    response = requests.post(
        "https://sharepanda.org/api/files/upload",
        headers=headers,
        files=file_handles,
        data={"is_public": "true"}
    )
    
    if response.status_code == 200:
        result = response.json()
        print(f"✅ {result['message']}")
        for uploaded_file in result.get('files', []):
            print(f"  - {uploaded_file['filename']}: {uploaded_file['file_id']}")
        
        # Check for any errors
        if 'errors' in result:
            print("❌ Some files failed:")
            for error in result['errors']:
                print(f"  - {error['filename']}: {error['error']}")
    else:
        print(f"Error: {response.json()['error']}")
finally:
    # Close all file handles
    for _, file_handle in file_handles:
        file_handle.close()`)}>Copy</button>
              </div>
              <pre><code>{`import requests

API_KEY = "apk_your_api_key_here"
FILES_TO_UPLOAD = [
    "/path/to/app-v1.0.apk",
    "/path/to/app-v1.0.ipa",
    "/path/to/release-notes.pdf"
]

headers = {"X-API-Key": API_KEY}

# Open all files
file_handles = [('file', open(path, 'rb')) for path in FILES_TO_UPLOAD]

try:
    response = requests.post(
        "https://sharepanda.org/api/files/upload",
        headers=headers,
        files=file_handles,
        data={"is_public": "true"}
    )
    
    if response.status_code == 200:
        result = response.json()
        print(f"✅ {result['message']}")
        for uploaded_file in result.get('files', []):
            print(f"  - {uploaded_file['filename']}: {uploaded_file['file_id']}")
        
        # Check for any errors
        if 'errors' in result:
            print("❌ Some files failed:")
            for error in result['errors']:
                print(f"  - {error['filename']}: {error['error']}")
    else:
        print(f"Error: {response.json()['error']}")
finally:
    # Close all file handles
    for _, file_handle in file_handles:
        file_handle.close()`}</code></pre>
            </div>

            <h4>Node.js</h4>
            <div className="code-block">
              <div className="code-header">
                <span>JavaScript - Single File</span>
                <button className="copy-btn" onClick={() => navigator.clipboard.writeText(`const FormData = require('form-data');
const fs = require('fs');
const axios = require('axios');

const API_KEY = 'apk_your_api_key_here';
const FILE_PATH = '/path/to/your/app.apk';

const form = new FormData();
form.append('file', fs.createReadStream(FILE_PATH));
form.append('is_public', 'true');

axios.post('https://sharepanda.org/api/files/upload', form, {
  headers: {
    'X-API-Key': API_KEY,
    ...form.getHeaders()
  }
})
.then(response => {
  console.log('File uploaded!', response.data);
  console.log('File ID:', response.data.file_id);
})
.catch(error => {
  console.error('Upload failed:', error.response?.data || error.message);
});`)}>Copy</button>
              </div>
              <pre><code>{`const FormData = require('form-data');
const fs = require('fs');
const axios = require('axios');

const API_KEY = 'apk_your_api_key_here';
const FILE_PATH = '/path/to/your/app.apk';

const form = new FormData();
form.append('file', fs.createReadStream(FILE_PATH));
form.append('is_public', 'true');

axios.post('https://sharepanda.org/api/files/upload', form, {
  headers: {
    'X-API-Key': API_KEY,
    ...form.getHeaders()
  }
})
.then(response => {
  console.log('File uploaded!', response.data);
  console.log('File ID:', response.data.file_id);
})
.catch(error => {
  console.error('Upload failed:', error.response?.data || error.message);
});`}</code></pre>
            </div>

            <div className="code-block">
              <div className="code-header">
                <span>JavaScript - Multiple Files</span>
                <button className="copy-btn" onClick={() => navigator.clipboard.writeText(`const FormData = require('form-data');
const fs = require('fs');
const axios = require('axios');

const API_KEY = 'apk_your_api_key_here';
const FILES_TO_UPLOAD = [
  '/path/to/app-v1.0.apk',
  '/path/to/app-v1.0.ipa',
  '/path/to/release-notes.pdf'
];

const form = new FormData();

// Append all files
FILES_TO_UPLOAD.forEach(filePath => {
  form.append('file', fs.createReadStream(filePath));
});

form.append('is_public', 'true');

axios.post('https://sharepanda.org/api/files/upload', form, {
  headers: {
    'X-API-Key': API_KEY,
    ...form.getHeaders()
  }
})
.then(response => {
  console.log('✅', response.data.message);
  response.data.files.forEach(file => {
    console.log(\`  - \${file.filename}: \${file.file_id}\`);
  });
  
  if (response.data.errors) {
    console.log('❌ Some files failed:');
    response.data.errors.forEach(error => {
      console.log(\`  - \${error.filename}: \${error.error}\`);
    });
  }
})
.catch(error => {
  console.error('Upload failed:', error.response?.data || error.message);
});`)}>Copy</button>
              </div>
              <pre><code>{`const FormData = require('form-data');
const fs = require('fs');
const axios = require('axios');

const API_KEY = 'apk_your_api_key_here';
const FILES_TO_UPLOAD = [
  '/path/to/app-v1.0.apk',
  '/path/to/app-v1.0.ipa',
  '/path/to/release-notes.pdf'
];

const form = new FormData();

// Append all files
FILES_TO_UPLOAD.forEach(filePath => {
  form.append('file', fs.createReadStream(filePath));
});

form.append('is_public', 'true');

axios.post('https://sharepanda.org/api/files/upload', form, {
  headers: {
    'X-API-Key': API_KEY,
    ...form.getHeaders()
  }
})
.then(response => {
  console.log('✅', response.data.message);
  response.data.files.forEach(file => {
    console.log(\`  - \${file.filename}: \${file.file_id}\`);
  });
  
  if (response.data.errors) {
    console.log('❌ Some files failed:');
    response.data.errors.forEach(error => {
      console.log(\`  - \${error.filename}: \${error.error}\`);
    });
  }
})
.catch(error => {
  console.error('Upload failed:', error.response?.data || error.message);
});`}</code></pre>
            </div>

            <h3>Response</h3>
            <h4>Single File Upload Response</h4>
            <div className="code-block">
              <div className="code-header">
                <span>JSON Response (Success)</span>
              </div>
              <pre><code>{`{
  "success": true,
  "message": "1 file(s) uploaded successfully",
  "files": [
    {
      "filename": "app.apk",
      "file_id": "a1b2c3d4e5f6g7h8",
      "success": true
    }
  ]
}`}</code></pre>
            </div>

            <h4>Multiple Files Upload Response (All Success)</h4>
            <div className="code-block">
              <div className="code-header">
                <span>JSON Response</span>
              </div>
              <pre><code>{`{
  "success": true,
  "message": "3 file(s) uploaded successfully",
  "files": [
    {
      "filename": "app-v1.0.apk",
      "file_id": "a1b2c3d4e5f6g7h8",
      "success": true
    },
    {
      "filename": "app-v1.0.ipa",
      "file_id": "i9j0k1l2m3n4o5p6",
      "success": true
    },
    {
      "filename": "release-notes.pdf",
      "file_id": "q7r8s9t0u1v2w3x4",
      "success": true
    }
  ]
}`}</code></pre>
            </div>

            <h4>Partial Success Response (HTTP 207)</h4>
            <div className="code-block">
              <div className="code-header">
                <span>JSON Response</span>
              </div>
              <pre><code>{`{
  "success": true,
  "message": "2 file(s) uploaded, 1 failed",
  "files": [
    {
      "filename": "app-v1.0.apk",
      "file_id": "a1b2c3d4e5f6g7h8",
      "success": true
    },
    {
      "filename": "app-v1.0.ipa",
      "file_id": "i9j0k1l2m3n4o5p6",
      "success": true
    }
  ],
  "errors": [
    {
      "filename": "large-video.mp4",
      "error": "Storage quota exceeded"
    }
  ]
}`}</code></pre>
            </div>
          </section>

          {/* File Management */}
          <section className="docs-section">
            <h2>Step 3: Manage Files</h2>

            <h3>List Your Files</h3>
            <div className="endpoint-box">
              <span className="method get">GET</span>
              <code>/api/files/my-files</code>
            </div>

            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -X GET https://sharepanda.org/api/files/my-files \\
  -H "X-API-Key: apk_your_api_key_here"`}</code></pre>
            </div>

            <h3>Get File Information</h3>
            <div className="endpoint-box">
              <span className="method get">GET</span>
              <code>/api/files/&#123;file_id&#125;</code>
            </div>

            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -X GET https://sharepanda.org/api/files/a1b2c3d4e5f6g7h8 \\
  -H "X-API-Key: apk_your_api_key_here"`}</code></pre>
            </div>

            <h3>Download URL</h3>
            <p>Private files:</p>
            <div className="code-block">
              <pre><code>https://sharepanda.org/api/files/&#123;file_id&#125;/download</code></pre>
            </div>

            <p>Public files:</p>
            <div className="code-block">
              <pre><code>https://sharepanda.org/api/files/public/&#123;username&#125;/&#123;file_id&#125;/download</code></pre>
            </div>

            <h3>Update File Visibility</h3>
            <div className="endpoint-box">
              <span className="method put">PUT</span>
              <code>/api/files/&#123;file_id&#125;/visibility</code>
            </div>

            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -X PUT https://sharepanda.org/api/files/a1b2c3d4e5f6g7h8/visibility \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"is_public": true}'`}</code></pre>
            </div>

            <h3>Delete File</h3>
            <div className="endpoint-box">
              <span className="method delete">DELETE</span>
              <code>/api/files/&#123;file_id&#125;</code>
            </div>

            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -X DELETE https://sharepanda.org/api/files/a1b2c3d4e5f6g7h8 \\
  -H "X-API-Key: apk_your_api_key_here"`}</code></pre>
            </div>
          </section>

          {/* Use Cases */}
          <section className="docs-section">
            <h2>Common Use Cases</h2>

            <h3>1. CI/CD Pipeline Integration</h3>
            <p>Automatically upload build artifacts after successful builds:</p>
            
            <div className="code-block">
              <div className="code-header">
                <span>GitHub Actions Example</span>
              </div>
              <pre><code>{`name: Upload Build to SharePanda

on:
  push:
    tags:
      - 'v*'

jobs:
  build-and-upload:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      
      - name: Build APK
        run: ./gradlew assembleRelease
      
      - name: Upload to SharePanda
        env:
          SHAREPANDA_API_KEY: \${{ secrets.SHAREPANDA_API_KEY }}
        run: |
          curl -X POST https://sharepanda.org/api/files/upload \\
            -H "X-API-Key: $SHAREPANDA_API_KEY" \\
            -F "file=@app/build/outputs/apk/release/app-release.apk" \\
            -F "is_public=true"`}</code></pre>
            </div>

            <h3>2. Mobile App Beta Distribution</h3>
            <p>Share APK/IPA files with beta testers:</p>
            <ol>
              <li>Upload your APK/IPA with <code>is_public=true</code></li>
              <li>Get the public download URL from the response</li>
              <li>Share the URL with your beta testers</li>
              <li>Track downloads via the dashboard</li>
            </ol>

            <h3>3. File Backup & Archive</h3>
            <p>Use SharePanda as a backup for important files:</p>
            <div className="code-block">
              <div className="code-header">
                <span>Bash Script</span>
              </div>
              <pre><code>{`#!/bin/bash
# Daily backup script

BACKUP_FILE="backup-$(date +%Y%m%d).tar.gz"
API_KEY="apk_your_api_key_here"

# Create backup
tar -czf $BACKUP_FILE /path/to/important/files

# Upload to SharePanda
curl -X POST https://sharepanda.org/api/files/upload \\
  -H "X-API-Key: $API_KEY" \\
  -F "file=@$BACKUP_FILE" \\
  -F "is_public=false"

# Cleanup local backup
rm $BACKUP_FILE`}</code></pre>
            </div>
          </section>

          {/* Storage Tiers */}
          <section className="docs-section">
            <h2>Storage Tiers</h2>
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Storage Limit</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Free</strong></td>
                  <td>100 MB</td>
                  <td>$0/month</td>
                </tr>
                <tr>
                  <td><strong>Basic</strong></td>
                  <td>1 GB</td>
                  <td>Contact us</td>
                </tr>
                <tr>
                  <td><strong>Pro</strong></td>
                  <td>10 GB</td>
                  <td>Contact us</td>
                </tr>
                <tr>
                  <td><strong>Enterprise</strong></td>
                  <td>1 TB</td>
                  <td>Contact us</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Error Codes */}
          <section className="docs-section">
            <h2>Error Handling</h2>
            
            <h3>Common Error Codes</h3>
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Status Code</th>
                  <th>Error</th>
                  <th>Solution</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>400</code></td>
                  <td>Bad Request</td>
                  <td>Check your request parameters and file format</td>
                </tr>
                <tr>
                  <td><code>401</code></td>
                  <td>Unauthorized</td>
                  <td>Verify your API key is correct and included in headers</td>
                </tr>
                <tr>
                  <td><code>403</code></td>
                  <td>Forbidden</td>
                  <td>Storage quota exceeded - upgrade your tier</td>
                </tr>
                <tr>
                  <td><code>404</code></td>
                  <td>Not Found</td>
                  <td>File or resource doesn't exist</td>
                </tr>
                <tr>
                  <td><code>207</code></td>
                  <td>Multi-Status</td>
                  <td>Partial success - some files uploaded, others failed. Check response for details.</td>
                </tr>
                <tr>
                  <td><code>413</code></td>
                  <td>Payload Too Large</td>
                  <td>File exceeds 2GB limit</td>
                </tr>
                <tr>
                  <td><code>500</code></td>
                  <td>Server Error</td>
                  <td>Contact support if issue persists</td>
                </tr>
              </tbody>
            </table>

            <h3>Error Response Format</h3>
            <div className="code-block">
              <div className="code-header">
                <span>JSON Response (Error)</span>
              </div>
              <pre><code>{`{
  "success": false,
  "error": "Storage quota exceeded"
}`}</code></pre>
            </div>
          </section>

          {/* Projects API */}
          <section className="docs-section">
            <h2>Projects API</h2>
            <p>
              Manage your SharePanda projects programmatically. Create, update, and publish projects via the API.
            </p>

            <h3>List My Projects</h3>
            <div className="endpoint-box">
              <span className="method get">GET</span>
              <code>/api/projects/my</code>
            </div>
            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -H "X-API-Key: apk_your_api_key_here" \\
  https://sharepanda.org/api/projects/my`}</code></pre>
            </div>

            <h3>Get Project</h3>
            <div className="endpoint-box">
              <span className="method get">GET</span>
              <code>/api/projects/&#123;project_id&#125;</code>
            </div>
            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -H "X-API-Key: apk_your_api_key_here" \\
  https://sharepanda.org/api/projects/your-project-id`}</code></pre>
            </div>

            <h3>Get Project by Slug</h3>
            <div className="endpoint-box">
              <span className="method get">GET</span>
              <code>/api/projects/slug/&#123;slug&#125;</code>
            </div>

            <h3>Update Project</h3>
            <div className="endpoint-box">
              <span className="method put">PUT</span>
              <code>/api/projects/&#123;project_id&#125;</code>
            </div>
            <p>Update any of these fields:</p>
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr><td><code>title</code></td><td>string</td><td>Project title</td></tr>
                <tr><td><code>short_description</code></td><td>string</td><td>One-line summary</td></tr>
                <tr><td><code>description</code></td><td>string</td><td>Full project description (Markdown supported)</td></tr>
                <tr><td><code>project_url</code></td><td>string</td><td>External project URL</td></tr>
                <tr><td><code>support_email</code></td><td>string</td><td>Support contact email</td></tr>
                <tr><td><code>pricing_type</code></td><td>string</td><td><code>free</code>, <code>fixed</code>, or <code>pwyw</code></td></tr>
                <tr><td><code>price</code></td><td>number</td><td>Price in the configured currency</td></tr>
                <tr><td><code>currency</code></td><td>string</td><td>Currency code (e.g. <code>USD</code>)</td></tr>
                <tr><td><code>crypto_addresses</code></td><td>object</td><td>Wallet addresses: <code>btc</code>, <code>usdt_trc20</code>, <code>usdt_bep20</code>, <code>eth</code>, <code>base</code></td></tr>
                <tr><td><code>is_public</code></td><td>boolean</td><td>Make project publicly visible</td></tr>
                <tr><td><code>screenshots</code></td><td>array</td><td>Screenshot metadata with <code>id</code> and <code>caption</code></td></tr>
                <tr><td><code>videos</code></td><td>array</td><td>Video metadata</td></tr>
              </tbody>
            </table>

            <h4>Example: Update description and pricing</h4>
            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -X PUT https://sharepanda.org/api/projects/your-project-id \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -d '{
    "short_description": "Probability-based trade scanner",
    "description": "## TradeGenie\\n\\nNo AI. Pure statistics.",
    "pricing_type": "pwyw",
    "price": 5,
    "currency": "USD"
  }'`}</code></pre>
            </div>

            <h4>Example: Update crypto wallets</h4>
            <div className="code-block">
              <div className="code-header">
                <span>cURL</span>
              </div>
              <pre><code>{`curl -X PUT https://sharepanda.org/api/projects/your-project-id \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: apk_your_api_key_here" \\
  -d '{
    "crypto_addresses": {
      "btc": "bc1q...",
      "usdt_trc20": "T...",
      "usdt_bep20": "0x...",
      "eth": "0x...",
      "base": "0x..."
    }
  }'`}</code></pre>
            </div>

            <h3>Agent / MCP Integration</h3>
            <p>
              SharePanda exposes an MCP server so AI agents can manage projects directly. Set the
              <code>SHAREPANDA_API_KEY</code> environment variable and point your MCP client at:
            </p>
            <div className="code-block">
              <div className="code-header">
                <span>Command</span>
              </div>
              <pre><code>/Users/siraj/.hermes/hermes-agent/venv/bin/python /Users/siraj/hosting/sharepanda/mcp-server/sharepanda_mcp.py</code></pre>
            </div>
          </section>

          {/* Best Practices */}
          <section className="docs-section">
            <h2>Best Practices</h2>
            
            <div className="best-practices">
              <div className="practice-item">
                <div>
                  <h4>Secure Your API Keys</h4>
                  <p>Store API keys in environment variables or secure vaults. Never commit them to version control.</p>
                </div>
              </div>

              <div className="practice-item">
                <div>
                  <h4>Use Descriptive Filenames</h4>
                  <p>Include version numbers and platform info in filenames (e.g., <code>myapp-v1.2.3-android.apk</code>)</p>
                </div>
              </div>

              <div className="practice-item">
                <div>
                  <h4>Implement Retry Logic</h4>
                  <p>Network issues happen. Implement exponential backoff for failed uploads.</p>
                </div>
              </div>

              <div className="practice-item">
                <div>
                  <h4>Monitor Your Usage</h4>
                  <p>Check your storage stats regularly via the dashboard or API.</p>
                </div>
              </div>

              <div className="practice-item">
                <div>
                  <h4>Clean Up Old Files</h4>
                  <p>Delete obsolete files to free up storage space for new uploads.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Rate Limits */}
          <section className="docs-section">
            <h2>Rate Limits</h2>
            <p>To ensure fair usage for all users, the following rate limits apply:</p>
            <ul>
              <li><strong>File Uploads:</strong> 100 requests per hour</li>
              <li><strong>API Requests:</strong> 1000 requests per hour</li>
              <li><strong>File Size:</strong> Maximum 2GB per file</li>
            </ul>
            <p>Contact us if you need higher limits for your use case.</p>
          </section>

          {/* Support */}
          <section className="docs-section">
            <h2>Support & Community</h2>
            <div className="support-grid">
              <div className="support-card">
                <h4>Email Support</h4>
                <p>Get help with technical issues</p>
                <a href="mailto:support@sharepanda.org" className="btn btn-primary btn-sm">
                  Contact Support
                </a>
              </div>

              <div className="support-card">
                <h4>Report Issues</h4>
                <p>Found a bug? Let us know</p>
                <a href="https://github.com/sharepanda/issues" className="btn btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">
                  GitHub Issues
                </a>
              </div>

              <div className="support-card">
                <h4>Feature Requests</h4>
                <p>Suggest new features</p>
                <a href="https://github.com/sharepanda/discussions" className="btn btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">
                  Discussions
                </a>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <section className="docs-section">
            <h2>Quick Links</h2>
            <div className="quick-links">
              <Link to="/register" className="quick-link-card">
                <div>
                  <h4>Get Started</h4>
                  <p>Create your free account</p>
                </div>
              </Link>

              <Link to="/api-keys" className="quick-link-card">
                <div>
                  <h4>API Keys</h4>
                  <p>Generate your API key</p>
                </div>
              </Link>

              <Link to="/dashboard" className="quick-link-card">
                <div>
                  <h4>Dashboard</h4>
                  <p>Monitor your usage</p>
                </div>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default DeveloperDocs;
